import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {
  GetForwardPointsResponse,
  HedgeType,
  IForwardPointsApiRequest,
  MarketRates,
  MultiHedge,
  QuotePair,
  RfqTab
} from '@approot/models';
import {
  HedgeQuoteMessage,
  ProductsConfigService,
  RfqTabService,
  SetupEventChange
} from '@approot/services';
import {
  clone,
  reformatDate,
  SubscriptionSink,
  uniq
} from '@approot/utilities';
import { Debounce } from 'lodash-decorators';
import { interval, Subscription } from 'rxjs';
import { RfqMultiHedgeComponent } from '../rfq-multi-hedge/rfq-multi-hedge.component';
import { HedgeExecutionMode, LiveHedgeRate } from '../rfq-setup/rfq-setup';
import { RfqSetupService } from '../rfq-setup/rfq-setup.service';

const DEFAULT_HEDGE_CLIENT: MultiHedge = {
  productType: 'None',
  offerDirection: 'Buy'
};

const DEFAULT_HEDGE_INTERNAL: MultiHedge = {
  productType: 'Forward',
  offerDirection: 'Buy'
};

@Component({
  selector: 'app-rfq-multi-hedge-group',
  templateUrl: './rfq-multi-hedge-group.component.html',
  styleUrls: ['./rfq-multi-hedge-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RfqMultiHedgeGroupComponent
  implements OnInit, OnDestroy, OnChanges
{
  @ViewChildren(RfqMultiHedgeComponent)
  public hedgeComponents: QueryList<RfqMultiHedgeComponent>;
  @Input() public groupName: string;
  @Input({ required: true }) public premiumCcy = '';
  @Input() public currencyPair: QuotePair;
  @Input() public hedgeMode = '';
  @Input() public premiumDate: string = '';
  @Input() public firstLegSettlementDate: string;
  @Input() public legSettlementDates: string[];
  @Input() public legExpiryDates: string[] = [];
  @Input() public presetHedges: MultiHedge[];
  @Input() public blockVolBasedPriceHedgeChanges = false;
  @Input() public pipsHighlightedIndices: number[] = [];
  @Input() public hedgeSetupValue: string;
  @Input() public marketRates: MarketRates;
  @Input() public hedgeQuote: HedgeQuoteMessage;
  @Output() public onCopyToInternal = new EventEmitter<MultiHedge[]>();
  @Output() public onHedgeChange = new EventEmitter<MultiHedge[]>();
  public isPreset = false;
  public reason: string;
  public rfqRunning = false;
  public isGenericMultiHedge = false;
  public isSettlementDatesSame = false;
  public firstLegExecutionMode: HedgeExecutionMode;
  public liveHedgeRates: LiveHedgeRate[];
  public spotRefsHidden = false;
  public fwdRatesHidden = false;
  public forwardPointsHidden = false;
  public showGenericMultiHedgeFields = false;

  private _hedges: MultiHedge[] = [];
  private subSink = new SubscriptionSink();
  private changeInExecutionApiSub: Subscription;
  constructor(
    public tabService: RfqTabService,
    private changeDetector: ChangeDetectorRef,
    private rfqTabService: RfqTabService,
    private rfqSetupService: RfqSetupService,
    private productsConfigService: ProductsConfigService
  ) {
    effect(_ => {
      this.rfqRunning = this.tabService.rfqRunning();
    });
  }

  public ngOnInit(): void {
    this.initMultiHedge();
    this.subSink.sink = interval(1000).subscribe(() => this.valid());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.hedgeMode?.currentValue) {
      this.changesInHedges();
    }

    if (changes.legSettlementDates?.currentValue) {
      this.isSettlementDatesSame = this.legSettlementDates?.every(
        (val, _, arr) => val === arr[0]
      );
    }

    if (
      changes.hedgeSetupValue?.currentValue &&
      this.rfqTabService.executionContextType() === 'genericMultiHedge'
    ) {
      this.showGenericMultiHedgeFields =
        !this.isSettlementDatesSame ||
        changes.hedgeSetupValue.currentValue === 'Custom Hedge';
      this.initMultiHedge();
    }
  }

  public ngOnDestroy(): void {
    this.changesInHedges();
    this.subSink.unsubscribe();
  }

  public initMultiHedge(): void {
    this.isGenericMultiHedge =
      this.rfqTabService.executionContextType() === 'genericMultiHedge';

    if (this.presetHedges) {
      this.isPreset = true;
      this._hedges = this.presetHedges;
      (this.rfqTabService.tabData as RfqTab).presetSetupData.hedges = null;
      return;
    }

    if (!this.tabService.tabData.title.quotePair) {
      return;
    }
    const isMedusaDeliverable = this.tabService.isMedusaDeliverable();
    if (this.hedges.length) {
      this.hedges = [];
      this.changeDetector.detectChanges();
    }
    if (this.isGenericMultiHedge) {
      this.legSettlementDates?.sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      );
    }
    if (isMedusaDeliverable && this.legSettlementDates?.length > 0) {
      const hedges = this.legSettlementDates.map(
        (settlementDate, index: number) => {
          return {
            ...DEFAULT_HEDGE_INTERNAL,
            settlementDate,
            expirationDate: settlementDate,
            legExpirationDate: this.legExpiryDates[index]
          };
        }
      );

      const filteredHedges =
        this.isGenericMultiHedge && this.hedgeSetupValue !== 'Custom Hedge'
          ? hedges.filter(
              (hedge, i) =>
                hedges.findIndex(
                  hedgesHedge =>
                    hedge.settlementDate === hedgesHedge.settlementDate
                ) === i
            )
          : [hedges[0]];

      const productType = this.defaultProductType(filteredHedges.length);
      filteredHedges.forEach(hedge => (hedge.productType = productType));
      this.addHedges(filteredHedges);
    } else if (this.legExpiryDates?.length > 0) {
      const hedges = this.legExpiryDates.map(
        (expirationDate, index: number) => {
          return {
            ...DEFAULT_HEDGE_INTERNAL,
            settlementDate: this.isGenericMultiHedge
              ? this.legSettlementDates[index]
              : this.firstLegSettlementDate,
            expirationDate,
            legExpirationDate: expirationDate
          };
        }
      );

      const filteredHedges =
        (isMedusaDeliverable && !this.isGenericMultiHedge) ||
        (this.isGenericMultiHedge && this.hedgeSetupValue === 'Custom Hedge')
          ? [hedges[0]]
          : hedges.filter(
              (hedge, i) =>
                hedges.findIndex(nonFilteredHedge =>
                  this.isGenericMultiHedge
                    ? hedge.settlementDate === nonFilteredHedge.settlementDate
                    : hedge.expirationDate === nonFilteredHedge.expirationDate
                ) === i
            );

      const productType = this.defaultProductType(filteredHedges.length);
      filteredHedges.forEach(hedge => (hedge.productType = productType));
      this.addHedges(filteredHedges);
    }
  }

  public addHedges(newHedges: MultiHedge[]) {
    const currentHedges = this.getHedges();
    this.hedges = clone([...currentHedges, ...newHedges]);
    this.changeDetector.markForCheck();
  }

  public set hedges(hedges: MultiHedge[]) {
    this._hedges = hedges;
    this.changeDetector.markForCheck();
    this.onHedgeChange.emit(hedges);
  }

  public get hedges() {
    return this._hedges;
  }

  public canAddHedge(): boolean {
    const hedges = this.getHedges();
    return (
      (hedges.length === 0 || hedges.every(h => h.productType !== 'None')) &&
      !(
        this.premiumCcy &&
        this.tabService.isMedusaDeliverable() &&
        hedges[0]?.productType === 'Spot'
      )
    );
  }

  public addHedge() {
    if (!this.canAddHedge() || this.rfqRunning || this.isGenericMultiHedge) {
      return;
    }
    this.tabService.removeAllVolBasedQuoteListeners();
    const hedges = this.getHedges();
    const newHedge = {
      ...DEFAULT_HEDGE_INTERNAL,
      productType: this.defaultProductType(hedges?.length + 1)
    };
    this.setDefaultSettlementDate(newHedge);
    this.addHedges([newHedge]);
    this.changesInHedges();
  }

  public deleteHedge(index: number) {
    this.tabService.removeAllVolBasedQuoteListeners();
    this.hedges = clone(this.hedges);
    this.hedges.splice(index, 1);
    // need to wait a tick for the view to update
    setTimeout(() => this.changeDetector.markForCheck(), 100);
    this.changesInHedges();
  }

  public valid(): boolean {
    this.reason = null;

    // 1. valid if no hedges
    const arr = this.hedgeComponents?.toArray() || [];
    if (arr.length === 0) {
      return true;
    }

    // 2. valid if all hedges are internally valid
    const allValid = arr.every(c => c.valid);
    if (!allValid) {
      this.reason = 'At least 1 hedge is invalid';
      this.changeDetector.markForCheck();
      return false;
    }

    // 3. Only allow 1 spot
    if (countOfType(arr, 'Spot') > 1) {
      this.reason = 'Cannot have more than 1 "Spot" hedge';
      this.changeDetector.markForCheck();
      return false;
    }

    // 4. If any hedge is "None", then can only allow 1 hedge in total.
    // Otherwise all hedges must be not "None"
    const numNoneHedges = countOfType(arr, 'None');
    if (numNoneHedges > 1 || (numNoneHedges === 1 && arr.length > 1)) {
      this.reason = 'If any hedge is "None", only allowed 1 hedge';
      this.changeDetector.markForCheck();
      return false;
    }
    this.changeDetector.markForCheck();

    return true;
  }

  public getRawHedges(): MultiHedge[] {
    return (this.hedgeComponents?.toArray() || []).map(c =>
      c.form.getRawValue()
    );
  }

  public getHedges(): MultiHedge[] {
    return (this.hedgeComponents?.toArray() || []).map(c => c.value());
  }

  private defaultProductType(nOfHedges: number): 'NDF' | 'Forward' | 'Spot' {
    return this.tabService.isMedusaDeliverable()
      ? nOfHedges > 1
        ? 'Forward'
        : 'Spot'
      : 'NDF';
  }

  public spotHedgeChange(hedgeChange: {
    type: HedgeType;
    index: number;
  }): void {
    if (hedgeChange.type !== 'Spot') {
      return;
    }

    const hedges = clone(this.getHedges());
    if (this.hedges.length === 1) {
      return;
    }
    this.hedges = hedges.filter(
      (_, index: number) => index === hedgeChange.index
    );
    this.changeDetector.markForCheck();
  }

  public changeInExecutionMode(
    firstLegExecutionMode: HedgeExecutionMode
  ): void {
    this.spotRefsHidden = true;
    this.fwdRatesHidden = true;
    this.forwardPointsHidden = true;
    this.firstLegExecutionMode = firstLegExecutionMode;
    switch (firstLegExecutionMode) {
      case 'Live - Forward Ref.':
      case 'DX - Forward Ref.':
        this.fwdRatesHidden = false;
        break;
      case 'Live - Spot + Pts Ref.':
      case 'Spot DX with Live pts':
        this.spotRefsHidden = false;
        this.fwdRatesHidden = false;
        this.forwardPointsHidden = false;
        break;
      default:
        break;
    }
    this.hedgeRatesApiCalls();
  }

  public changeInSpotRef(data: { key: string; value: string }): void {
    this.hedgeComponents?.forEach((component, index) => {
      if (index !== 0) {
        component.form.get(data.key).setValue(data.value);
      }
    });
  }

  public hedgeRatesApiCalls(): void {
    if (this.hedgeComponents?.length !== this.hedges.length) {
      this.liveHedgeRates = null;
      return;
    }
    const dates = uniq(
      this.hedgeComponents
        .map(hedge => hedge.form.get('settlementDate').value)
        .filter(settlementDate => settlementDate)
    );

    if (!dates.length) {
      return;
    }

    this.changeInExecutionApiSub?.unsubscribe();

    this.hedgeComponents.forEach(hedgeComponent => {
      hedgeComponent.fwdRateSettings.pending = true;
      hedgeComponent.spotRefSettings.pending = true;
      hedgeComponent.fwdPointsSettings.pending = true;
    });
    this.changeInExecutionApiSub = this.fwdPoint(dates).subscribe({
      next: (resp: GetForwardPointsResponse) => {
        const liveHedgeRates: LiveHedgeRate[] = [];
        this.hedgeComponents
          .map(hedge => hedge.form.get('settlementDate').value)
          .forEach((settlementDate, index: number) => {
            const cleanSettlementDate = reformatDate(
              settlementDate,
              'YYYY-MM-DD'
            );

            if (!cleanSettlementDate) {
              liveHedgeRates[index] = null;
            }
            const cross = `${this.currencyPair.base}${this.currencyPair.term}`;
            const fwdPointAsk =
              resp.forwards[cleanSettlementDate].forwardPoints.ask;
            const fwdPointBid =
              resp.forwards[cleanSettlementDate].forwardPoints.bid;
            const fwdPointMid =
              resp.forwards[cleanSettlementDate].forwardPoints.mid;
            const dp = this.productsConfigService.decimalPlacesForCross(cross);

            liveHedgeRates[index] = {
              spotRefAsk: resp.spot.ask.toFixed(dp),
              spotRefMid: resp.spot.mid.toFixed(dp),
              spotRefBid: resp.spot.bid.toFixed(dp),
              fwdPointAsk: fwdPointAsk.toFixed(2),
              fwdPointBid: fwdPointBid.toFixed(2),
              fwdPointMid: fwdPointMid.toFixed(2)
            };

            if (
              this.firstLegExecutionMode === 'DX - Forward Ref.' ||
              this.firstLegExecutionMode === 'Live - Forward Ref.'
            ) {
              liveHedgeRates[index]['fwdRateAsk'] =
                resp.forwards[cleanSettlementDate].outrightForward.ask.toFixed(
                  dp
                );
              liveHedgeRates[index]['fwdRateBid'] =
                resp.forwards[cleanSettlementDate].outrightForward.bid.toFixed(
                  dp
                );
              liveHedgeRates[index]['fwdRateMid'] =
                resp.forwards[cleanSettlementDate].outrightForward.mid.toFixed(
                  dp
                );
            }
          });
        this.liveHedgeRates = liveHedgeRates;
      },
      error: _ => (this.liveHedgeRates = null)
    });
  }

  @Debounce(250)
  public submitSetupEvent(change: SetupEventChange): void {
    const requestID = this.rfqTabService.rfqDetailsDTO?.id;
    if (!requestID || this.hedgeRatesPending()) {
      return;
    }
    this.rfqTabService.submitSetupEvent(requestID, { change });
  }

  public hedgeRatesPending(): boolean {
    return this.hedgeComponents
      .toArray()
      .some(
        hedge =>
          hedge.fwdRateSettings.pending ||
          hedge.spotRefSettings.pending ||
          hedge.fwdPointsSettings.pending
      );
  }

  private fwdPoint(expiryDates: string[]): IForwardPointsApiRequest {
    const cross = `${this.currencyPair.base}${this.currencyPair.term}`;
    return this.rfqSetupService.getLatestFwdRef(expiryDates, cross);
  }

  private async changesInHedges() {
    this.tabService.changeInHedges.next();
  }

  private setDefaultSettlementDate(newHedge: MultiHedge): void {
    const hedges = this.getHedges();

    if (
      this.rfqTabService.tabData.isVolBased &&
      newHedge.productType === 'Forward' &&
      hedges.length
    ) {
      const legSettlementDates = [...new Set(this.legSettlementDates)];
      const forwardDates = [
        ...new Set(
          hedges.map(x => reformatDate(x.settlementDate, 'D MMM YYYY'))
        )
      ];

      newHedge.settlementDate =
        legSettlementDates.find(x => !forwardDates.includes(x)) ||
        legSettlementDates[legSettlementDates.length - 1];
    }
  }
}

function countOfType(arr: RfqMultiHedgeComponent[], type: HedgeType): number {
  return arr.map(c => c.value().productType).filter(t => t === type).length;
}
